import React from 'react';
import { Button, DatePicker, Menu, Dropdown, Icon, Radio } from 'antd';
import queryString from 'query-string';
import { FormattedMessage, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import CreateMeetingContainer from '../../Containers/CreateMeeting/CreateMeetingContainer';
import CalendarSettingsContainer from '../../Containers/CalendarSettings/CalendarSettingsContainer';
import './NavbarComponent.scss';

const { WeekPicker } = DatePicker;
const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;
const messages = defineMessages({
  aryaCalendar: {
    id: 'Navbar.AryaCalendar',
    defaultMessage: 'Arya Calendar',
    values: {
      AppName: 'Arya',
    },
  },
  calendarSettings: {
    id: 'CalendarSettings.CalendarSettings',
    defaultMessage: 'Calendar settings',
  },
});

function selectTimePicker(props) {
  switch (props.calendarView) {
    case 'day':
      return (
        <DatePicker
          onChange={props.onDateChange}
          format="dddd, MMMM D"
          value={moment(props.dayValue)}
          allowClear={false}
          getCalendarContainer={triggerNode => triggerNode.parentNode}
          className="date-picker"
        />
      );
    case 'week':
      return (
        <WeekPicker
          onChange={props.onWeekChange}
          value={moment(props.weekValue)}
          format={`[${moment(props.weekValue)
            .startOf('week')
            .format('DD MMM')} - ${moment(props.weekValue)
            .endOf('week')
            .format('DD MMM')}]`}
          allowClear={false}
          getCalendarContainer={triggerNode => triggerNode.parentNode}
          className="time-picker"
        />
      );
    default:
      return (
        <DatePicker
          onChange={props.onDateChange}
          format="dddd, MMMM D"
          value={moment(props.dayValue)}
          allowClear={false}
          getCalendarContainer={triggerNode => triggerNode.parentNode}
          className="time-picker"
        />
      );
  }
}

selectTimePicker.propTypes = {
  dayValue: PropTypes.string.isRequired,
  weekValue: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onWeekChange: PropTypes.func.isRequired,
  calendarView: PropTypes.string.isRequired,
};

function setPreviousDate(props) {
  if (props.calendarView === 'day') {
    const previousDate = moment(props.dayValue).subtract(1, 'day');
    props.onDateChange(previousDate);
  } else {
    const previousWeek = moment(props.dayValue).subtract(1, 'week');
    props.onWeekChange(previousWeek);
  }
}

function selectCalendarLabel(props) {
  const label = [];
  let labelContainer;
  let weekDateLabel;
  let weekDayLabel;
  if (props.calendarView === 'week') {
    for (let index = 0; index < 7; index += 1) {
      weekDateLabel = (
        <div className="weekdate" key={`date${index}`}>
          {`${moment(props.weekValue)
            .startOf('week')
            .add(index, 'days')
            .format('D MMM')}`}
        </div>
      );
      weekDayLabel = (
        <div className="weekday" key={`day${index}`}>
          {`${moment(props.weekValue)
            .startOf('week')
            .add(index, 'days')
            .format('dddd')}`}
        </div>
      );
      label.push(
        <div className="week-daywise-label" key={index}>
          {weekDateLabel}
          {weekDayLabel}
        </div>
      );
    }
    labelContainer = <div className="label-container">{label}</div>;
  }
  return labelContainer;
}

selectCalendarLabel.propTypes = {
  weekValue: PropTypes.func.isRequired,
  calendarView: PropTypes.string.isRequired,
};

function setNextDate(props) {
  if (props.calendarView === 'day') {
    const nextDate = moment(props.dayValue).add(1, 'day');
    props.onDateChange(nextDate);
  } else {
    const nextWeek = moment(props.dayValue).add(1, 'week');
    props.onWeekChange(nextWeek);
  }
}

function NavbarComponent(props) {
  const [menuZoneRequest, setMenuZoneRequest] = React.useState(0);
  const { refreshCalendar, cssClassName, goToToday, openZoneRequest } = props;
  const { appname } = queryString.parse(window.location.search);
  const menu = (
    <Menu className="calendar-menu">
      <Menu.Item key="add-event" className="create-meeting-menu">
        <CreateMeetingContainer />
      </Menu.Item>
      <Menu.Item key="settings" onClick={() => setMenuZoneRequest(menuZoneRequest + 1)}>
        <Button type="primary" className="calendar-settings-button"><FormattedMessage {...messages.calendarSettings} /></Button>
      </Menu.Item>
    </Menu>
  );
  return (
    <div className={`navbar ${cssClassName}`}>
      <div className="navbar-actions">
        <span id="title">
          <FormattedMessage {...messages.aryaCalendar} values={{ AppName: appname || 'Arya' }} />
        </span>
        <span onClick={() => setPreviousDate(props)} role="presentation">
          <Icon type="left-circle" className="previous-date" />
        </span>
        {selectTimePicker(props)}
        <span onClick={() => setNextDate(props)} role="presentation">
          <Icon type="right-circle" className="next-date" />
        </span>
        <Dropdown overlay={menu} getPopupContainer={triggerNode => triggerNode.parentNode}>
          <Button id="options">
            <Icon type="ellipsis" className="menu-icon" />
          </Button>
        </Dropdown>
        <RadioGroup className="view-selector-radio-group" defaultValue="day">
          <RadioButton className="day-view" value="day" onClick={() => props.switchToDayView()}>
            Day
          </RadioButton>
          <RadioButton className="week-view" value="week" onClick={() => props.switchToWeekView()}>
            Week
          </RadioButton>
        </RadioGroup>
        <RadioButton className="today" onClick={goToToday}>
          Today
        </RadioButton>
      </div>
      {(openZoneRequest > 0 || menuZoneRequest > 0) && <CalendarSettingsContainer refreshCalendar={refreshCalendar} openZoneRequest={openZoneRequest + menuZoneRequest} focusZoneRequest={openZoneRequest} showTrigger={false} />}
      {selectCalendarLabel(props)}
    </div>
  );
}

NavbarComponent.propTypes = {
  openZoneRequest: PropTypes.number,
  switchToDayView: PropTypes.func.isRequired,
  switchToWeekView: PropTypes.func.isRequired,
  goToToday: PropTypes.func.isRequired,
  cssClassName: PropTypes.string.isRequired,
  dayValue: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onWeekChange: PropTypes.func.isRequired,
  weekValue: PropTypes.string.isRequired,
  refreshCalendar: PropTypes.func.isRequired,
};

NavbarComponent.defaultProps = { openZoneRequest: 0 };

export default NavbarComponent;
